import styled, { css } from 'styled-components';

import { ZIndex } from '@rover/constants/src/styles/ZIndex';
import type Color from '@rover/kibble/styles/Color';

export const TRANSITION_DURATION = 150;

type StyledPopoverProps = {
  backgroundColor?: Color | string;
  borderColor?: Color | string;
  showArrow?: boolean;
  isOutOfBoundaries?: boolean;
  maxWidth?: string;
};

const FORWARDED_DOM_PROPS = new Set(['style', 'className', 'role', 'children', 'id']);

const StyledPopover = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    FORWARDED_DOM_PROPS.has(prop) ||
    prop.startsWith('data-') ||
    prop.startsWith('aria-') ||
    prop.startsWith('on'),
})<StyledPopoverProps>`
  transition: opacity ${TRANSITION_DURATION}ms linear;
  opacity: 0;
  &[data-status='open'] {
    opacity: 1;
  }

  background-color: ${(props) => props.backgroundColor?.toString() ?? ''};
  text-align: left;
  border: 1px solid ${(props) => props.borderColor?.toString() ?? ''};
  border-radius: 4px;
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.2);
  padding: 8px;
  z-index: ${ZIndex.POPOVER.toString()};
  max-width: ${({ maxWidth }): string => maxWidth || '450px'};
  visibility: ${({ isOutOfBoundaries }) => (isOutOfBoundaries ? 'hidden' : 'inherit')};

  ${({ showArrow, borderColor, backgroundColor }) =>
    !showArrow
      ? ''
      : css`
          &:before,
          &:after {
            border: 1px solid ${borderColor?.toString() ?? ''};
            border: solid transparent;
            content: ' ';
            height: 0;
            width: 0;
            position: absolute;
            pointer-events: none;
          }

          &:after {
            border-color: rgba(255, 255, 255, 0);
            border-width: 8px;
          }

          &:before {
            border-color: rgba(204, 204, 204, 0);
            border-width: 9px;
          }

          &[data-placement='bottom'] {
            &:before,
            &:after {
              bottom: 100%;
              left: var(--arrow-x, 50%);
            }

            &:after {
              border-bottom-color: ${backgroundColor?.toString() ?? ''};
              margin-left: -8px;
            }

            &:before {
              border-bottom-color: ${borderColor?.toString() ?? ''};
              margin-left: -9px;
            }
          }

          &[data-placement='top'] {
            &:before,
            &:after {
              top: 100%;
              left: var(--arrow-x, 50%);
            }

            &:after {
              border-top-color: ${backgroundColor?.toString() ?? ''};
              margin-left: -8px;
            }

            &:before {
              border-top-color: ${borderColor?.toString() ?? ''};
              margin-left: -9px;
            }
          }

          &[data-placement='right'] {
            &:before,
            &:after {
              right: 100%;
              top: var(--arrow-y, 50%);
            }

            &:after {
              border-right-color: ${backgroundColor?.toString() ?? ''};
              margin-top: -8px;
            }

            &:before {
              border-right-color: ${borderColor?.toString() ?? ''};
              margin-top: -9px;
            }
          }

          &[data-placement='left'] {
            &:before,
            &:after {
              left: 100%;
              top: var(--arrow-y, 50%);
            }

            &:after {
              border-left-color: ${backgroundColor?.toString() ?? ''};
              margin-top: -8px;
            }

            &:before {
              border-left-color: ${borderColor?.toString() ?? ''};
              margin-top: -9px;
            }
          }
        `};
`;
export default StyledPopover;
